import {
  VERIFY_CODE_LENGTH,
  VERIFY_CODE_TTL_MINUTES,
  VERIFY_NOT_A_CODE_MAX,
  VERIFY_PREVIEW_CODE,
  VERIFY_RESEND_AFTER_S,
  VERIFY_WRONG_CODES_MAX,
  WORKFLOW_LIMITS,
} from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";
import type { VerifyEmailStep } from "../types";
import { StepChannelNotes } from "./channel-notes";
import { CountedTextarea } from "./counted-field";
import type { FlowContext } from "./flow-context";
import { PathSelect } from "./path-select";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const H = "engage.workflows.fields.verifyHow";

/** The facts an author needs to place the step well: which address, how long, when it fails, what a tester types. */
function HowItWorks() {
  const { t } = useTranslation();
  const facts = [
    t(`${H}.address`, { digits: VERIFY_CODE_LENGTH }),
    t(`${H}.lifetime`, { minutes: VERIFY_CODE_TTL_MINUTES }),
    t(`${H}.failures`, { wrong: VERIFY_WRONG_CODES_MAX, other: VERIFY_NOT_A_CODE_MAX }),
    t(`${H}.resend`, { seconds: VERIFY_RESEND_AFTER_S }),
    t(`${H}.verified`),
  ];
  return (
    <section className="space-y-2" aria-labelledby="verify-how-title" data-testid="verify-how">
      <h4 id="verify-how-title" className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t(`${H}.title`)}</h4>
      <ul className="space-y-1.5 rounded-md bg-surface-2 p-3 text-xs text-fg-muted">
        {facts.map((fact) => (
          <li key={fact} className="flex items-start gap-2">
            <Icon name="shield-check" className="mt-0.5 size-3.5 shrink-0 text-primary" />
            <span>{fact}</span>
          </li>
        ))}
        <li className="flex items-start gap-2" data-testid="verify-preview-code">
          <Icon name="flask" className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" />
          <span>{t(`${H}.preview`, { code: VERIFY_PREVIEW_CODE })}</span>
        </li>
      </ul>
    </section>
  );
}

/**
 * Verify e-mail: the visitor gets a one-time code at the address the workflow already knows and types it in the chat.
 * The author writes the lead-in; the code, the masked address and the buttons come from the server.
 */
export function VerifyEmailForm({ step, disabled, onChange, paths, canAddPath }: FormProps<VerifyEmailStep> & Pick<FlowContext, "paths" | "canAddPath">) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div>
        <CountedTextarea label={t(`${F}.verifyIntro`)} hint={t(`${F}.verifyIntroHint`)} rows={3} value={step.text} max={WORKFLOW_LIMITS.message} disabled={disabled} onChange={(text) => { onChange({ ...step, text }); }} />
        <VariableList />
      </div>
      <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="verify-collect-first">{t(`${F}.verifyCollectFirst`)}</p>
      <PathSelect label={t(`${F}.verifiedGoTo`)} hint={t(`${F}.verifiedHint`)} value={step.verified_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.nextStep`)} disabled={disabled} onChange={(verified_goto) => { onChange({ ...step, verified_goto }); }} />
      <PathSelect label={t(`${F}.failedGoTo`)} hint={t(`${F}.failedHint`)} value={step.failed_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.endHere`)} disabled={disabled} onChange={(failed_goto) => { onChange({ ...step, failed_goto }); }} />
      <HowItWorks />
      <StepChannelNotes type="verify_email" />
    </div>
  );
}
